import { useState, useEffect } from "react";
import "./PaymentAccount.css";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import toast from "react-hot-toast";
import CircularProgress from "@mui/material/CircularProgress";
import { useNavigate, useLoaderData } from "react-router-dom";
import {
  generateStripeLink,
  isStripeConnected,
  paymentHistory,
} from "../../../../services/payment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import AssuredWorkloadIcon from "@mui/icons-material/AssuredWorkload";
import HttpsIcon from "@mui/icons-material/Https";
import { timestampToDateTime } from "../../../../utils";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@material-ui/core/Typography";
import { Box } from "@mui/material";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";

const countryCode = [
  { name: "Austria", code: "AT" },
  { name: "Belgium", code: "BE" },
  { name: "Bulgaria", code: "BG" },
  { name: "Croatia", code: "HR" },
  { name: "Cyprus", code: "CY" },
  { name: "Czechia", code: "CZ" },
  { name: "Denmark", code: "DK" },
  { name: "Estonia", code: "EE" },
  { name: "Finland", code: "FI" },
  { name: "France", code: "FR" },
  { name: "Germany", code: "DE" },
  { name: "Greece", code: "EL" },
  { name: "Hungary", code: "HU" },
  { name: "Ireland", code: "IE" },
  { name: "Italy", code: "IT" },
  { name: "Latvia", code: "LV" },
  { name: "Lithuania", code: "LT" },
  { name: "Luxembourg", code: "LU" },
  { name: "Malta", code: "MT" },
  { name: "Netherlands", code: "NL" },
  { name: "Poland", code: "PL" },
  { name: "Portugal", code: "PT" },
  { name: "Romania", code: "RO" },
  { name: "Slovakia", code: "SK" },
  { name: "Slovenia", code: "SI" },
  { name: "Spain", code: "ES" },
  { name: "Sweden", code: "SE" },
];

export const PaymentAccount = () => {
  const user = useLoaderData();

  const [loading, setLoading] = useState(true);
  const [linkLoading, setLinkLoading] = useState(false);
  const [accountStatus, setAccountStatus] = useState("default");
  const [code, setCode] = useState(null);
  const [linkData, setLinkData] = useState(null);
  const [accountData, setAccountData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(true);

  const handleChange = (event) => {
    setCode(event.target.value);
  };

  const createPaymentAccount = async () => {
    setLinkLoading(true);
    const res = await generateStripeLink(user?.token, code);
    // console.log("link res: ", res);

    if (res?.status === true) {
      setLinkData(res?.data);
      setLinkLoading(false);
      return;
    }

    if (res?.status === false) {
      if (res.message === "Invalid or expired access token.") {
        toast.error("Token expired Please re-login and try");
        setLinkLoading(false);
        return;
      }

      toast.error("Something went wrong, please try again later.");
      setLinkLoading(false);
      return;
    }

    toast.error("Something went wrong, please try again later.");
    setLinkLoading(false);
    return;
  };

  const getAccountInfo = async () => {
    const res = await isStripeConnected(user?.token);
    // console.log("res: ", res);

    if (res?.isVerified === false) {
      setAccountStatus(false);
      setLoading(false);
      return;
    }

    if (res?.isVerified === true) {
      setAccountData(res?.data);
      setAccountStatus(true);
      setLoading(false);
      return;
    }

    toast.error("Something went wrong please try again later");
    setLoading(false);
    return;
  };

  const getPaymentHistory = async () => {
    const res = await paymentHistory(user?.token);
    // console.log("payment history res: ", res);

    if (res?.status === true) {
      setPaymentData(res?.data);
      setPaymentLoading(false);
      return;
    }

    if (res?.status === false) {
      if (res.message === "Invalid or expired access token.") {
        toast.error("Token expired Please re-login and try");
        setPaymentLoading(false);
        return;
      }

      toast.error("Something went wrong, please try again later.");
      setPaymentLoading(false);
      return;
    }

    toast.error("Something went wrong, please try again later.");
    setPaymentLoading(false);
    return;
  };

  useEffect(() => {
    getAccountInfo();
    getPaymentHistory();
  }, []);

  return (
    <>
      <Box style={{ marginBottom: '30px' }}>
				<Typography variant="h3" className="page-title">
					Payment Account Information
				</Typography>
				<Grid container spacing={3}>
					<Grid item xs={12} md={12} lg={12}>
						<Card sx={{ p: 0 }}>
							<CardContent>
                {loading ? (
                  <Box sx={{ textAlign: "center" }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {accountStatus === true && (
                      <>
                        <h4 style={{ color: "green", marginTop: "0" }}>
                          <HttpsIcon fontSize="small" /> You have successfully connected your account with stripe.
                        </h4>
                        <div className="payment-accoutn-info">
                          <Box component="div" className="data-row">
                            <div className="row-label">Name</div>
                            <div className="row-data">{`${accountData?.individual?.first_name} ${accountData?.individual?.last_name}`}</div>
                          </Box>
                          <Box component="div" className="data-row">
                            <div className="row-label">Email</div>
                            <div className="row-data">{accountData?.individual?.email}</div>
                          </Box>
                          <Box component="div" className="data-row">
                            <div className="row-label">Account Id</div>
                            <div className="row-data">{accountData?.individual?.account}</div>
                          </Box>
                          <Box component="div" className="data-row">
                            <div className="row-label">Country Code</div>
                            <div className="row-data">{accountData?.country}</div>
                          </Box>
                          <Box component="div" className="data-row">
                            <div className="row-label">Default Currency</div>
                            <div className="row-data">{accountData?.default_currency}</div>
                          </Box>
                          <Box component="div" className="data-row">
                            <div className="row-label">Created At</div>
                            <div className="row-data">{timestampToDateTime(accountData?.individual?.created)}</div>
                          </Box>
                        </div>
                      </>
                    )}
                    {accountStatus === false && (
                      <>
                        <h3>You haven't created Stripe Connect account yet, Please select your country and click on button.</h3>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                          <FormControl sx={{ width: "100%", maxWidth: '200px' }} size="small">
                            <InputLabel id="code-label">Select country</InputLabel>
                            <Select
                              labelId="code-label"
                              id="demo-simple-select"
                              value={code}
                              label="Select country"
                              onChange={handleChange}
                            >
                              {countryCode?.map((item) => (
                                <MenuItem value={item?.code}>{item?.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button
                            disabled={code === null}
                            className="btn theme-btn"
                            variant="contained"
                            color="primary"
                            onClick={createPaymentAccount}
                            loading={linkLoading}
                            loadingPosition="end"
                          >
                            submit
                          </Button>
                        </div>
                      </>
                    )}
                  </>
                )}

                {linkData && (
                  <>
                    <h5>Account Id: {linkData?.accountId}</h5>
                    <a href={linkData?.accountLink} target="_blank">
                      {linkData?.accountLink}
                    </a>
                    <p>To send and receive payments through Yachtmate, you need to create a Stripe Connect account. Please click the link above to complete the setup.</p>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* payment history */}
			<Box>
				<Typography variant="h3" className="page-title">
					Payment History
				</Typography>
				<Grid container spacing={3}>
					<Grid  item xs={12} md={12} lg={12}>
            {paymentLoading ? (
              <Card>
              <CardContent sx={{ textAlign: "center" }}>
                <CircularProgress />
              </CardContent>
              </Card>
            ) : (
              <>
                {paymentData?.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 700 }} aria-label="simple table">
                      <TableHead>
                        <TableRow>
                          <TableCell>Berth</TableCell>
                          <TableCell align="right">Type</TableCell>
                          <TableCell align="right">User yacht</TableCell>
                          <TableCell align="right">User Email</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="right">Payment Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paymentData.map((row) => (
                          <TableRow
                            key={row?.id}
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                            }}
                          >
                            <TableCell component="th" scope="row">
                              {row?.sourceBerth?.name}
                            </TableCell>
                            <TableCell align="right">
                              {row?.transactionType}
                            </TableCell>
                            <TableCell align="right">
                              {row?.userYachtName}
                            </TableCell>
                            <TableCell align="right">{row?.userEmail}</TableCell>
                            <TableCell align="right">
                              {row?.totalAmount}
                            </TableCell>
                            <TableCell align="right">
                              {row?.isPaid ? "Paid" : "Failed"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Card>
                    <CardContent sx={{ textAlign: "center" }}>
                      <h3>Payment history not found!</h3>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
					</Grid>
				</Grid>
			</Box>
    </>
  );
};
